import type { ReactNode } from 'react'

import { track, type CardType } from '../../../analytics'
import { ArtifactAction } from './ArtifactAction'
import { ARTIFACT_EYEBROW, ARTIFACT_META, ARTIFACT_SURFACE } from './styles'

/**
 * The shared artifact card: warm surface, amber eyebrow, and one right-hand
 * slot that is either an in-app deep link or a line of meta text.
 *
 * `Link` rather than `<a>`: an artifact href is always an app route
 * (`/sessions`, `/builder`, `/knowledge-base`), and a full page load would
 * throw away the streamed conversation. Any test that renders an artifact
 * must therefore wrap it in a router.
 *
 * **A card with an action is itself the target** (SH-4 of the 2026-10-15 web
 * design review): the link stretches over the card, and the card answers a
 * pointer, a press and the keyboard (`ARTIFACT_CARD_LINK`). A card with only
 * meta text, or nothing, is not pressable and does not react.
 *
 * **Analytics (#708):** a card that names its `cardType` reports `coach_card_opened` when its link is followed, with the
 * type and nothing of what the card shows. A card with no action has no link to follow and reports nothing.
 */
export function ArtifactCard({
  eyebrow,
  action,
  meta,
  cardType,
  children,
}: {
  eyebrow: string
  action?: { label: string; href: string } | null
  meta?: string | null
  cardType?: CardType
  children: ReactNode
}) {
  return (
    <div style={ARTIFACT_SURFACE} className={action ? 'relative' : undefined}>
      <div className="flex items-baseline justify-between gap-3" style={{ marginBottom: 10 }}>
        <span style={ARTIFACT_EYEBROW}>{eyebrow}</span>
        {action ? (
          <ArtifactAction
            href={action.href}
            label={action.label}
            stretch
            onFollow={cardType ? () => track('coach_card_opened', { card_type: cardType }) : undefined}
          />
        ) : meta ? (
          <span style={ARTIFACT_META}>{meta}</span>
        ) : null}
      </div>
      {children}
    </div>
  )
}
